// src/constants/adMob.ts
import { Capacitor } from '@capacitor/core';
import { FEATURES } from './features';

export type RoddoAdPlacement = 'baleen-bay-feed' | 'search-lure-reward';

export const ADMOB_TEST_APP_IDS = {
  android: 'ca-app-pub-3940256099942544~3347511713',
  ios: 'ca-app-pub-3940256099942544~1458002511',
} as const;

export const ADMOB_TEST_REWARDED_AD_UNIT_IDS = {
  android: 'ca-app-pub-3940256099942544/5224354917',
  ios: 'ca-app-pub-3940256099942544/1712485313',
} as const;

export const ADMOB_TEST_NATIVE_AD_UNIT_IDS = {
  android: 'ca-app-pub-3940256099942544/2247696110',
  ios: 'ca-app-pub-3940256099942544/3986624511',
} as const;

export const ADMOB_PRODUCTION_REWARDED_AD_UNIT_IDS = {
  android: 'ca-app-pub-3992122948595637/4789580170',
  ios: 'ca-app-pub-3992122948595637/3955096376',
} as const;

export const REWARDED_SEARCH_LURE_AMOUNT = 3;
export const REWARDED_SEARCH_LURE_DAILY_CAP = 5;

// Real Roddo AdMob ad unit IDs remain gated by FEATURES.
// Rewarded Search Lures may run in production while Baleen Bay native ads stay OFF.
export const ADMOB_PRODUCTION_NATIVE_AD_UNIT_IDS = {
  android: 'ca-app-pub-3992122948595637/3784061868',
  ios: 'ca-app-pub-3992122948595637/2085791215',
} as const;

export const RODDO_ADMOB = {
  enabled: Boolean(FEATURES.ADS_ENABLED),
  testMode: Boolean(FEATURES.ADS_TEST_MODE),
  baleenBayNativeAdsEnabled: Boolean(
    FEATURES.ADS_ENABLED && FEATURES.BALEEN_BAY_NATIVE_ADS_ENABLED,
  ),
  rewardedSearchLuresEnabled: Boolean(
    FEATURES.ADS_ENABLED && FEATURES.REWARDED_SEARCH_LURE_ADS_ENABLED,
  ),
  feedInterval: 2,
} as const;

export function getRoddoAdPlatform(): 'android' | 'ios' | 'web' {
  try {
    const platform = Capacitor.getPlatform();
    if (platform === 'android' || platform === 'ios') return platform;
  } catch {}
  return 'web';
}

export function canUseNativeAdMob(): boolean {
  try {
    return Capacitor.isNativePlatform() && getRoddoAdPlatform() !== 'web';
  } catch {
    return false;
  }
}

export function getNativeAdUnitId(placement: RoddoAdPlacement): string | null {
  if (placement !== 'baleen-bay-feed') return null;

  const platform = getRoddoAdPlatform();
  if (platform !== 'android' && platform !== 'ios') return null;

  if (RODDO_ADMOB.testMode) return ADMOB_TEST_NATIVE_AD_UNIT_IDS[platform];

  return ADMOB_PRODUCTION_NATIVE_AD_UNIT_IDS[platform] || null;
}

export function getRewardedSearchLureAdUnitId(): string | null {
  const platform = getRoddoAdPlatform();
  if (platform !== 'android' && platform !== 'ios') return null;

  if (RODDO_ADMOB.testMode) return ADMOB_TEST_REWARDED_AD_UNIT_IDS[platform];
  return ADMOB_PRODUCTION_REWARDED_AD_UNIT_IDS[platform] || null;
}
